import useGeneralStore from "@/store/General/useGeneralStore";
import Head from "next/head";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import {
  SITE_URL,
  SITE_NAME,
  ORGANIZATION_NAME,
  SITE_ALTERNATE_NAMES,
  SHARE_IMAGE,
  pageUrl,
  cleanPath,
  jsonBody,
  excerpt,
  articleDescription,
  articleAlternates,
  absoluteUrl,
  safeJson,
  plainText,
} from "@/lib/seo";

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  keywords?: string;
  noindex?: boolean;
  canonical?: string;
}
export default function SEOHead({
  title,
  description,
  image,
  noindex = false,
  canonical,
}: SEOHeadProps) {
  const router = useRouter();
  const { t } = useTranslation("common");
  const state = useGeneralStore();
  const locale = router.locale || "ar";
  const path = cleanPath(router.asPath);
  const meta = jsonBody(state.MetaComponent);
  const home = jsonBody(state.HomeComponent);
  const siteName =
    meta.title ||
    state.HomeComponent?.title ||
    home.heroSlider?.slides?.[0]?.title ||
    (locale === "en"
      ? "Yad Albaraka Foundation for Social Services and Development"
      : ORGANIZATION_NAME);
  const siteDescription =
    meta.description ||
    state.HomeComponent?.description ||
    home.heroSlider?.slides?.[0]?.subtitle ||
    "";
  const article =
    path.startsWith("/blogs/") &&
    state.BlogDetailComponent?.id === Number(router.query.id)
      ? state.BlogDetailComponent
      : null;
  const service = path.startsWith("/services/")
    ? jsonBody(state.ServicesComponent).services?.find(
        (s: any) => s.slug === router.query.slug,
      )
    : null;
  const about = jsonBody(state.AboutComponent);
  const apiDescription =
    path === "/"
      ? home.mission?.desc
      : path === "/about"
        ? about.content
            ?.find((node: any) => node.type === "p")
            ?.children?.map((leaf: any) => leaf.text || "")
            .join("")
        : path === "/events"
          ? jsonBody(state.EventsComponent).header?.description
          : path === "/services"
            ? home.services?.subtitle
            : path === "/contact"
              ? state.ContactComponent?.description
              : path === "/volunteer"
                ? jsonBody(state.VolunteerComponent).header?.subtitle
                : undefined;
  const labels: Record<string, string> = {
    "/": siteName,
    "/about": t("about.header.title"),
    "/services": t("services.title"),
    "/events": t("events.title"),
    "/news": t("news.title"),
    "/contact": t("contact.header.title"),
    "/volunteer": t("volunteer.title"),
  };
  const name =
    article?.title || service?.title || title || labels[path] || siteName;
  const brandTitle = locale === "en" ? "Yad Albaraka" : SITE_NAME;
  const pageTitle =
    name && siteName && name !== siteName ? `${name} | ${brandTitle}` : name;
  const pageDescription = article
    ? articleDescription(article)
    : excerpt(
        service?.description ||
          apiDescription ||
          description ||
          siteDescription,
      );
  const selectedImage =
    image ||
    article?.imageUrl ||
    service?.imgUrl ||
    meta.imageUrl ||
    SHARE_IMAGE;
  // Cloudinary generates a real 1200×630 crop rather than merely declaring that size.
  const pageImage = absoluteUrl(
    selectedImage.includes("res.cloudinary.com/") &&
      selectedImage.includes("/image/upload/")
      ? selectedImage.replace(
          "/image/upload/",
          "/image/upload/c_fill,g_auto,w_1200,h_630,q_auto,f_jpg/",
        )
      : selectedImage,
  );
  const url = canonical ? absoluteUrl(canonical) : pageUrl(path, locale);
  const privatePage =
    noindex ||
    router.pathname.startsWith("/dashboard") ||
    ["/login", "/404", "/500", "/photos", "/videos"].includes(router.pathname);
  const alternates = article
    ? articleAlternates(article)
    : { ar: pageUrl(path, "ar"), en: pageUrl(path, "en") };
  const sameAs = Array.from(
    new Set(
      (state.SocialLinksComponent || [])
        .map((s: any) => s.url || jsonBody(s).url || jsonBody(s).href)
        .filter((value: unknown): value is string => {
          if (typeof value !== "string") return false;
          try {
            const url = new URL(value);
            const host = url.hostname.replace(/^(www|m)\./, "");
            // Platform homepages do not identify the charity's social profiles.
            const platform = [
              "facebook.com",
              "fb.com",
              "instagram.com",
              "linkedin.com",
              "twitter.com",
              "x.com",
              "youtube.com",
              "tiktok.com",
            ].includes(host);
            return (
              url.protocol === "https:" && !(platform && url.pathname === "/")
            );
          } catch {
            return false;
          }
        }),
    ),
  );
  const orgId = `${SITE_URL}/#organization`,
    websiteId = `${SITE_URL}/#website`;
  const graph: any[] = [
    {
      "@type": "Organization",
      "@id": orgId,
      name: siteName,
      alternateName: [SITE_NAME, ...SITE_ALTERNATE_NAMES],
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: absoluteUrl("/icons/logo.png") },
      ...(sameAs.length ? { sameAs } : {}),
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: SITE_URL,
      name: SITE_NAME,
      alternateName: SITE_ALTERNATE_NAMES,
      publisher: { "@id": orgId },
      inLanguage: ["ar", "en"],
    },
    {
      "@type":
        path === "/about"
          ? "AboutPage"
          : path === "/contact"
            ? "ContactPage"
            : path === "/news" || path === "/services"
              ? "CollectionPage"
              : "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: pageTitle,
      description: pageDescription,
      inLanguage: locale,
      isPartOf: { "@id": websiteId },
      primaryImageOfPage: { "@type": "ImageObject", url: pageImage },
    },
  ];
  if (path !== "/" && !privatePage) {
    const crumbs = [
      {
        "@type": "ListItem",
        position: 1,
        name: t("header.nav.home"),
        item: pageUrl("/", locale),
      },
    ];
    if (article || service)
      crumbs.push({
        "@type": "ListItem",
        position: 2,
        name: article ? t("news.title") : t("services.title"),
        item: pageUrl(article ? "/news" : "/services", locale),
      });
    crumbs.push({
      "@type": "ListItem",
      position: crumbs.length + 1,
      name,
      item: url,
    });
    graph.push({ "@type": "BreadcrumbList", itemListElement: crumbs });
  }
  if (article) {
    const body = jsonBody(article);
    const date = (value: string) =>
      value && !Number.isNaN(Date.parse(value))
        ? new Date(value).toISOString()
        : undefined;
    graph.push({
      "@type": "Article",
      "@id": `${url}#article`,
      headline: plainText(article.title),
      description: pageDescription,
      image: [pageImage],
      inLanguage: locale,
      mainEntityOfPage: { "@id": `${url}#webpage` },
      publisher: { "@id": orgId },
      ...(body.author
        ? { author: { "@type": "Person", name: body.author } }
        : {}),
      ...(date(article.createdAt)
        ? { datePublished: date(article.createdAt) }
        : {}),
      ...(date(article.updatedAt)
        ? { dateModified: date(article.updatedAt) }
        : {}),
    });
  }
  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} key="description" />
      <meta
        name="robots"
        content={
          privatePage
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
        key="robots"
      />
      <link rel="canonical" href={url} key="canonical" />
      {!privatePage &&
        Object.entries(alternates).map(([lang, href]) => (
          <link
            key={`alternate-${lang}`}
            rel="alternate"
            hrefLang={lang}
            href={href}
          />
        ))}
      {!privatePage && alternates.ar && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={alternates.ar}
          key="alternate-default"
        />
      )}
      <meta
        property="og:type"
        content={article ? "article" : "website"}
        key="og:type"
      />
      <meta property="og:url" content={url} key="og:url" />
      <meta property="og:title" content={pageTitle} key="og:title" />
      <meta
        property="og:description"
        content={pageDescription}
        key="og:description"
      />
      <meta property="og:image" content={pageImage} key="og:image" />
      <meta
        property="og:image:secure_url"
        content={pageImage}
        key="og:image:secure_url"
      />
      <meta property="og:image:width" content="1200" key="og:image:width" />
      <meta property="og:image:height" content="630" key="og:image:height" />
      <meta property="og:image:alt" content={name} key="og:image:alt" />
      <meta property="og:site_name" content={SITE_NAME} key="og:site_name" />
      <meta
        property="og:locale"
        content={locale === "ar" ? "ar_EG" : "en_US"}
        key="og:locale"
      />
      <meta
        property="og:locale:alternate"
        content={locale === "ar" ? "en_US" : "ar_EG"}
        key="og:locale:alternate"
      />
      {article && (
        <meta
          property="article:section"
          content={jsonBody(article).category || t("news.title")}
          key="article:section"
        />
      )}
      <meta
        name="twitter:card"
        content="summary_large_image"
        key="twitter:card"
      />
      <meta name="twitter:title" content={pageTitle} key="twitter:title" />
      <meta
        name="twitter:description"
        content={pageDescription}
        key="twitter:description"
      />
      <meta name="twitter:image" content={pageImage} key="twitter:image" />
      <meta name="twitter:image:alt" content={name} key="twitter:image:alt" />
      {!privatePage && siteName && (
        <script
          type="application/ld+json"
          key="structured-data"
          dangerouslySetInnerHTML={{
            __html: safeJson({
              "@context": "https://schema.org",
              "@graph": graph,
            }),
          }}
        />
      )}
    </Head>
  );
}
